// client/src/views/Sidebar_parts/POISection.js
import React, { useState } from "react";

function POISection({
  customCategories,
  customTypes,
  typeColours,
  pois,
  visibleTypes,
  visibleCategories,
  expandedCategories,
  setExpandedCategories,
  expandedTypes,
  setExpandedTypes,
  toggleAllTypes,
  allTypesVisible,
  toggleCategoryExpand,
  toggleCategory,
  toggleType,
  isMobile,
  onMobileTypeClick,
  handleTypeHover,
  handleTypeUnhover,
  setHideSidebarHover,
  setHoveredPoiId,
  fontColour,
  mainFont,
  sectionNamePOIs,
  hasSearch,
  normalizedSearch,
}) {
  const [lockedType, setLockedType] = useState(null);
  const [lockedPoiId, setLockedPoiId] = useState(null);

  const hasPois = Array.isArray(pois) && pois.length > 0;
  if (!hasPois) return null;

  const handleTypeClick = (type) => {
    if (isMobile) {
      if (typeof onMobileTypeClick === "function") {
        onMobileTypeClick(type);
      }
      return;
    }
    if (
      typeof handleTypeHover !== "function" ||
      typeof handleTypeUnhover !== "function"
    ) {
      return;
    }
    setLockedType((prev) => {
      if (prev === type) {
        handleTypeUnhover();
        return null;
      } else {
        handleTypeHover(type);
        return type;
      }
    });
  };

  const handlePoiClick = (poiId) => {
    if (!setHoveredPoiId) return;
    setLockedPoiId((prev) => {
      if (prev === poiId) {
        setHoveredPoiId(null);
        return null;
      } else {
        setHoveredPoiId(poiId);
        return poiId;
      }
    });
  };

  return (
    <>
      <h3 style={{ color: fontColour, fontFamily: mainFont }}>
        {sectionNamePOIs}
      </h3>
      <button onClick={toggleAllTypes} style={{ marginBottom: "10px" }}>
        {allTypesVisible ? "Hide All" : "Show All"}
      </button>

      {customCategories.map((category) => {
        const categoryLabel = category || "—";
        const typesInCategory = customTypes[category] || [];

        // Count only POIs linked to this category
        const categoryPois = pois.filter(
          (p) => p.category === category
        );

        // Category has zero POIs? Hide it entirely.
        if (categoryPois.length === 0) return null;

        // Filter types: must contain at least one visible POI
        const typesToRender = typesInCategory.filter((type) => {
          const poisOfType = pois.filter(
            (p) => p.category === category && p.type === type
          );

          // No POIs of this type = hide it completely
          if (poisOfType.length === 0) return false;

          if (!hasSearch) return true;

          const typeMatchesSearch = type
            .toLowerCase()
            .includes(normalizedSearch);

          const categoryMatchesSearch =
            categoryLabel.toLowerCase().includes(normalizedSearch);

          const poiMatches = poisOfType.some((p) =>
            (p.poi_name || "").toLowerCase().includes(normalizedSearch)
          );

          return typeMatchesSearch || categoryMatchesSearch || poiMatches;
        });

        // Category has no visible types → hide it
        if (typesToRender.length === 0) return null;

        const categoryMatchesSearch =
          hasSearch &&
          categoryLabel.toLowerCase().includes(normalizedSearch);

        if (hasSearch && !categoryMatchesSearch && typesToRender.length === 0) {
          return null;
        }

        const isCategoryExpanded = hasSearch
          ? true
          : !!expandedCategories[category];

        return (
          <div key={category}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <span
                onClick={(e) => toggleCategoryExpand(category, e)}
                style={{
                  marginRight: 5,
                  cursor: "pointer",
                  userSelect: "none",
                  fontWeight: 700,
                  fontSize: "1.2em",
                }}
                tabIndex={0}
                role="button"
                aria-label={isCategoryExpanded ? "Collapse" : "Expand"}
              >
                {isCategoryExpanded ? "▼" : "►"}
              </span>
              <input
                type="checkbox"
                checked={visibleCategories.has(category)}
                onChange={() => toggleCategory(category)}
                style={{ marginRight: 5 }}
              />
              <strong>{categoryLabel}</strong>
            </div>

            {isCategoryExpanded && (
              <ul>
                {typesToRender.map((type) => {
                  const color = typeColours[type] || "#ff0000";
                  const poisOfType = pois.filter(
                    (p) => p.category === category && p.type === type
                  );

                  const matchingPois = hasSearch
                    ? poisOfType.filter((poi) => {
                        const dn =
                          typeof poi.poi_name === "string"
                            ? poi.poi_name.toLowerCase()
                            : "";
                        return (
                          dn.includes(normalizedSearch) ||
                          type.toLowerCase().includes(normalizedSearch) ||
                          categoryLabel
                            .toLowerCase()
                            .includes(normalizedSearch)
                        );
                      })
                    : poisOfType;

                  const count = matchingPois.length;

                  // Hide type if there are no POIs after search
                  if (count === 0) return null;

                  const isTypeExpanded = hasSearch
                    ? true
                    : !!expandedTypes[type];

                  return (
                    <li
                      key={type}
                      style={{
                        marginBottom: 3,
                        cursor: isMobile ? "pointer" : "default",
                      }}
                      onMouseEnter={() => {
                        if (
                          typeof handleTypeHover === "function" &&
                          (!lockedType || lockedType === type)
                        ) {
                          handleTypeHover(type);
                        }
                        if (setHideSidebarHover) setHideSidebarHover(true);
                      }}
                      onMouseLeave={() => {
                        if (
                          typeof handleTypeUnhover === "function" &&
                          lockedType !== type
                        ) {
                          handleTypeUnhover();
                        }
                        if (setHideSidebarHover) setHideSidebarHover(false);
                      }}
                    >
                      <span
                        onClick={() => {
                          setExpandedTypes((prev) => ({
                            ...prev,
                            [type]: !prev[type],
                          }));
                        }}
                        style={{
                          cursor: "pointer",
                          marginRight: 6,
                          userSelect: "none",
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label={
                          isTypeExpanded ? "Collapse POIs" : "Expand POIs"
                        }
                      >
                        {isTypeExpanded ? "▼" : "►"}
                      </span>
                      <input
                        type="checkbox"
                        checked={visibleTypes.has(type)}
                        onChange={() => toggleType(type)}
                      />
                      <span
                        style={{
                          display: "inline-block",
                          width: "10px",
                          height: "10px",
                          backgroundColor: color,
                          marginLeft: "6px",
                          marginRight: "6px",
                          borderRadius: "2px",
                        }}
                      />
                      <span
                        onClick={() => handleTypeClick(type)}
                        style={{
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                      >
                        {type} ({count})
                      </span>

                      {isTypeExpanded && matchingPois.length > 0 && (
                        <ul style={{ marginLeft: 24, marginTop: 2 }}>
                          {matchingPois.map((poi) => {
                            const displayName =
                              typeof poi.poi_name === "string" &&
                              poi.poi_name.trim() !== ""
                                ? poi.poi_name
                                : "(unnamed)";
                            const titleText = `${poi.category || "—"} • ${
                              poi.type || "—"
                            } • ${displayName}`;
                            return (
                              <li
                                key={poi.id}
                                className="sidebar-poi-item"
                                onMouseEnter={() => {
                                  if (
                                    setHoveredPoiId &&
                                    (!lockedPoiId || lockedPoiId === poi.id)
                                  ) {
                                    setHoveredPoiId(poi.id);
                                  }
                                  setHideSidebarHover &&
                                    setHideSidebarHover(true);
                                }}
                                onMouseLeave={() => {
                                  if (
                                    setHoveredPoiId &&
                                    lockedPoiId !== poi.id
                                  ) {
                                    setHoveredPoiId(null);
                                  }
                                  setHideSidebarHover &&
                                    setHideSidebarHover(false);
                                }}
                                onClick={() => handlePoiClick(poi.id)}
                                title={titleText}
                              >
                                <div className="sidebar-poi-content">
                                  <span
                                    className="sidebar-poi-icon"
                                    style={{ backgroundColor: color }}
                                  />
                                  <span className="sidebar-poi-label">
                                    {displayName}
                                  </span>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </>
  );
}

export default POISection;
